<script>
/**
 * Card de KPI padronizado (piloto). Substitui stat.vue / blocos .totals / mini-cards feitos à mão.
 * Grade recomendada: <div class="st-kpi-grid"> (2 colunas no celular, 4 no desktop).
 */
export default {
  name: "StKpiCard",
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: "—" },
    hint: { type: String, default: "" },
    icon: { type: String, default: "" },
    variant: {
      type: String,
      default: "brand",
      validator: (v) => ["brand", "success", "warning", "danger", "info", "neutral"].indexOf(v) !== -1,
    },
    loading: { type: Boolean, default: false },
  },
};
</script>

<template>
  <div class="st-kpi" :class="'st-kpi-' + variant" :aria-busy="loading ? 'true' : 'false'">
    <div class="st-kpi-top">
      <span class="st-kpi-label">{{ label }}</span>
      <span v-if="icon" class="st-kpi-icon" aria-hidden="true"><i :class="icon"></i></span>
    </div>
    <div v-if="loading" class="st-kpi-skeleton" aria-hidden="true"></div>
    <div v-else class="st-kpi-value">{{ value }}</div>
    <div v-if="hint && !loading" class="st-kpi-hint">{{ hint }}</div>
    <slot></slot>
  </div>
</template>

<style scoped>
.st-kpi {
  --kpi-accent: var(--st-brand);
  --kpi-soft: var(--st-brand-soft);
  position: relative;
  height: 100%;
  padding: 0.875rem 1rem;
  background: var(--st-surface);
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
  box-shadow: var(--st-shadow-xs);
  overflow: hidden;
}
.st-kpi::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: var(--kpi-accent);
}
.st-kpi-success { --kpi-accent: var(--st-success); --kpi-soft: var(--st-success-soft); }
.st-kpi-warning { --kpi-accent: var(--st-warning); --kpi-soft: var(--st-warning-soft); }
.st-kpi-danger { --kpi-accent: var(--st-danger); --kpi-soft: var(--st-danger-soft); }
.st-kpi-info { --kpi-accent: var(--st-info); --kpi-soft: var(--st-brand-soft); }
.st-kpi-neutral { --kpi-accent: var(--st-border); --kpi-soft: var(--st-canvas); }

.st-kpi-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}
.st-kpi-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--st-text-muted);
}
.st-kpi-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex: 0 0 2rem;
  border-radius: 50%;
  background: var(--kpi-soft);
  color: var(--st-brand);
  font-size: 1.125rem;
}
.st-kpi-value {
  margin-top: 0.25rem;
  font-size: 1.625rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--st-text);
  font-variant-numeric: tabular-nums;
}
.st-kpi-hint {
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.st-kpi-skeleton {
  margin-top: 0.5rem;
  height: 1.5rem;
  width: 60%;
  border-radius: var(--st-radius-sm);
  background: linear-gradient(90deg, var(--st-canvas), var(--st-border-soft), var(--st-canvas));
  background-size: 200% 100%;
  animation: st-kpi-shimmer 1.2s ease-in-out infinite;
}
@keyframes st-kpi-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .st-kpi-skeleton { animation: none; }
}
@media (max-width: 575.98px) {
  .st-kpi { padding: 0.75rem 0.875rem; }
  .st-kpi-value { font-size: 1.375rem; }
}
</style>
